import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Button } from '@mui/material';
import { useNavigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import useSubscribeRefresh from 'use-subscribe-actions'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import EmptyScreen from 'packages/ui/EmptyScreen';

import CenterTable from './Table/CenterTable';
import Filters from '../Filters';
import Card from './Card/Card';

import MainContent, { ResponsiveSideContent, TableContent } from 'components-shared';
import { FIND_CENTER_DOCUMENTS } from '../operations-gql/queries';
import useFilters from '../store';
import { useUser, useSites } from 'store';

const LIMIT = 20;

const CenterDocuments = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filters] = useFilters();
  const [user] = useUser();
  const { id } = useParams();
  const [{ selected }] = useSites();
  const { pathname } = useLocation();

  useSubscribeRefresh(() => navigate('/documents/center'));
  const editMode = pathname.includes('/edit');

  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true);

  const sfIds = useMemo(
    () => selected ? selected.map(({ sf_id }) => sf_id) : [],
    [selected],
  );

  const getDefaultTypes = () => {
    const defaultTypes = [];
    if (user && user.roles) {
      if (user.roles.documents_financial_report) defaultTypes.push('financial_report');
      if (user.roles.documents_indicator_report) defaultTypes.push('indicator_report');
      if (user.roles.documents_support_documentation) defaultTypes.push('support_document');
      if (user.roles.documents_other_documents) defaultTypes.push('other');
    }
    return defaultTypes.join(',');
  };  

  const { loading, data, refetch, fetchMore, networkStatus } = useQuery(FIND_CENTER_DOCUMENTS, {
    variables: {
      query: filters.verbatim || '',
      sort_by: null,
      sort_direction: null,
      limit: LIMIT,
      offset: 0,
      document_type: filters.type || getDefaultTypes(),
      created_at_start: filters.createdAt?.start ? new Date(filters.createdAt.start).toISOString() : null,
      created_at_end: filters.createdAt?.end ? new Date(filters.createdAt.end).toISOString() : null,
      sf_account_ids: sfIds.length > 0 ? sfIds : [],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache'
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const documents = useMemo(() => {
    if (!data) return [];
    return data.centerDocuments.map(doc => ({
      ...doc,
      site_name: doc.site?.name || '',
      created_at: formatDate(doc.created_at),
      updated_at: formatDate(doc.updated_at),
    }))
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch, filters, sfIds]);

  const handleRowClick = (doc) => {
    navigate(`/documents/center/${doc.id}`);
  };

  const fetchNextPage = async () => {
    const fetchMoreResult = await fetchMore({
      variables: {
        offset: documents.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          centerDocuments: [...prev.centerDocuments, ...fetchMoreResult.centerDocuments]
        };
      },
    });
    if (
      !fetchMoreResult.data.centerDocuments.length ||
      fetchMoreResult.data.centerDocuments.length < LIMIT
    ) {
      setShouldFetchNextPage(false);
    }
  };

  const handleRefetch = async (variables) => {
    await refetch({
      ...variables,
      created_at_start: filters.createdAt?.start ? new Date(filters.createdAt.start).toISOString() : null,
      created_at_end: filters.createdAt?.end ? new Date(filters.createdAt.end).toISOString() : null,
      sf_account_ids: sfIds.length > 0 ? sfIds : [],
    });
    setShouldFetchNextPage(true);
  };

  return (
    <Stack flex={1}>
      <Stack direction="row" width={'100%'} justifyContent="space-between" alignItems="center">
        <Filters apiCount={null} />
        {(user.admin || (user.roles && user.roles.wifirst_documents)) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<i className={'fa-regular fa-plus'} />}
            onClick={() => navigate('create', { state: { modal: true } })}
            sx={{
              minHeight: '32px',
              borderRadius: '6px',
              fontSize: '12px',
              '& .MuiButton-startIcon': {
                '& i': {
                  fontSize: '12px',
                },
              },
            }}
          >
            {t('documents.actions.add')}
          </Button>
        )}
      </Stack>
      <MainContent>
        <TableContent>
          {loading && networkStatus !== 3 ? (
            <TableSkeleton />
          ) : !documents || documents.length === 0 ? (
            <EmptyScreen icon="file">{t('documents.no_documents_filter')}</EmptyScreen>
          ) : (
            <CenterTable
              documentSelected={id}
              queryDocuments={{
                loading: loading && networkStatus !== 3,
                loadingNext: networkStatus === 3,
                data: { documents },
                refetch: handleRefetch,
                fetchNextPage,
                shouldFetchNextPage: !loading && documents.length >= LIMIT && shouldFetchNextPage,
              }}
              onRowClick={handleRowClick}
            />
          )}
        </TableContent>
        {id && !editMode && (
          <ResponsiveSideContent padding={0} onClose={() => navigate('/documents/center')}>
            <Card id={id} onClose={() => navigate('/documents/center')} />
          </ResponsiveSideContent>
        )}
        <Outlet />
      </MainContent>
    </Stack>
  );
};

export default CenterDocuments;
